import { type Feature } from 'core/domain/Feature'
import { type MarketTag } from 'core/domain/MarketTag'
import { type Mode } from 'core/domain/Mode'
import { type TruckType } from 'core/domain/Truck'
import { formatFeatureLabel } from 'core/presenters/featurePresenter'
import { formatMarketTagLabel } from 'core/presenters/marketTagPresenter'
import { getTruckLabel } from 'core/presenters/truckPresenter'
import { type FilterSummaryItem } from 'core/ui/components/filter-summary/FilterSummary'
import { toArray } from 'core/utils/array'
import { isEmpty, isString } from 'core/utils/string'

import { type FilterState } from '../ui/filter/LoadboardFilters'

function formatEquipment(equipments?: Array<TruckType>) {
  return toArray(equipments).map(getTruckLabel).join(', ')
}

function formatFeatures(features?: Array<Feature>) {
  return toArray(features).map(formatFeatureLabel).join(', ')
}

function formatLane(pickup?: string, delivery?: string, filterByPreferredLanes?: boolean) {
  if (filterByPreferredLanes) return 'Preferred lanes'

  if (!pickup && !delivery) return ''

  return `${pickup ?? 'Anywhere'} → ${delivery ?? 'Anywhere'}`
}

function formatMode(modes?: Array<Mode>) {
  return toArray(modes).slice().join(', ')
}

function formatPeriod(pickupDateAfter?: string, pickupDateBefore?: string) {
  return [pickupDateAfter, pickupDateBefore].filter(isString).join(' → ')
}

function formatType(types?: Array<MarketTag>) {
  return toArray(types).map(formatMarketTagLabel).join(', ')
}

export function formatFilterSummaryItems(filter: FilterState): Array<FilterSummaryItem> {
  const {
    pickup,
    delivery,
    radius,
    filterByPreferredLanes,
    pickupDateRangeStart,
    pickupDateRangeEnd,
    modes,
    equipments,
    type,
    features,
  } = filter

  return [
    { id: 'lane', label: 'Lane', value: formatLane(pickup, delivery, filterByPreferredLanes) },
    { id: 'radius', label: 'Miles', value: String(radius ?? '') },
    {
      id: 'period',
      label: 'Period',
      value: formatPeriod(pickupDateRangeStart, pickupDateRangeEnd),
    },
    { id: 'modes', label: 'Mode', value: formatMode(modes) },
    { id: 'equipments', label: 'Equipment', value: formatEquipment(equipments) },
    { id: 'type', label: 'Type', value: formatType(type) },
    { id: 'features', label: 'Feature', value: formatFeatures(features) },
  ].filter(({ value }) => !isEmpty(value))
}
